import { Link } from "react-router-dom";
import FaqSchema from "@/components/FaqSchema";
import ResourceLayout, { AnswerBlock, DataTable } from "@/components/ResourceLayout";

const FAQS = [
  {
    q: "What is a good food cost percentage for a fine dining restaurant?",
    a: "A healthy fine dining target sits between 28 and 34 percent of menu price. Casual dining runs 30 to 35 percent, bars run 18 to 24 percent. Below 25 percent usually means the plate is under portioned, above 38 percent means the price is too low or waste is too high.",
  },
  {
    q: "How do you calculate food cost percentage?",
    a: "Divide the total ingredient cost of a plated dish by the sell price and multiply by one hundred. For example, a plate that costs six dollars and fifty cents to produce and sells for twenty two dollars carries a food cost of 29.5 percent.",
  },
  {
    q: "How do I lower a plate's food cost without cheapening the dish?",
    a: "Raise the yield: recheck edible yield percent on trimmed proteins and produce, cross utilise trim in sauces or stocks, and reweigh portions on a scale rather than by eye. Renegotiate the top three costliest SKUs, then adjust the menu price only after those two levers are pulled.",
  },
];

export default function FoodCost() {
  return (
    <ResourceLayout
      activeSlug="food-cost"
      title="Food Cost: how the professional kitchen prices a plate"
      lede="A working reference for chefs, sous chefs, and owner operators. Standard formulas, edible yield tables, and the plain-language method every station line cook can memorise before service."
    >
      <FaqSchema
        id="food-cost"
        title="Food Cost Formulas and Percentages for Restaurants | Culinaire Specialist"
        description="Standard food cost formulas, target percentages by service style, and edible yield tables for professional kitchens. A working reference for chefs and owner operators."
        canonicalUrl="https://www.culinairespecialist.com/resources/food-cost"
        faqs={FAQS}
      />

      <AnswerBlock
        id="what-is-food-cost"
        heading="What is food cost and why does it matter?"
        answer="Food cost is the money a restaurant spends on the ingredients used to produce a finished dish, expressed as a percentage of the price that dish sells for. It is the single largest controllable variable on a restaurant profit and loss statement, ahead of even labour in most kitchens."
        softwareNote={<>Track it live: Culinaire Specialist recomputes every plate's food cost the moment a supplier price moves, so a shift in butter or beef never quietly bleeds your margin. <Link to="/register" className="gold underline">Start free</Link>.</>}
      />

      <AnswerBlock
        id="how-to-calculate-food-cost"
        heading="How to calculate food cost percentage on any menu item"
        answer="Add every ingredient's cost per usable gram or millilitre, multiply by the portion used in the recipe, then divide by the plate's menu price. The result multiplied by one hundred is the food cost percentage. Edible yield must be applied first so trim loss is not undercounted."
      >
        <DataTable
          caption="Common food cost percentage targets"
          columns={["Service style", "Healthy food cost", "Warning zone"]}
          rows={[
            ["Fine dining", "28 to 34 percent", "Above 36 percent"],
            ["Casual dining", "30 to 35 percent", "Above 38 percent"],
            ["Quick service", "28 to 32 percent", "Above 34 percent"],
            ["Coffee and cafe", "22 to 30 percent", "Above 34 percent"],
            ["Bar and cocktails", "18 to 24 percent", "Above 26 percent"],
            ["Bakery and pastry", "25 to 32 percent", "Above 36 percent"],
          ]}
          testId="food-cost-table-targets"
        />
      </AnswerBlock>

      <AnswerBlock
        id="edible-yield"
        heading="Why edible yield changes your real cost per gram"
        answer="Raw purchase price does not equal usable cost. A whole beef tenderloin loses roughly one third of its mass to trim, silverskin, and connective tissue. Failing to apply the edible yield percent to the purchase price silently understates the true cost of every plate that touches that protein."
      >
        <DataTable
          caption="Typical edible yield percentages by ingredient"
          columns={["Ingredient", "Typical edible yield", "Standard trim loss"]}
          rows={[
            ["Whole beef tenderloin", "62 to 68 percent", "Silverskin, chain, tail"],
            ["Whole Atlantic salmon", "50 to 60 percent", "Head, bones, skin, pin bones"],
            ["Whole free-range chicken", "70 to 78 percent", "Carcass, wing tips, feet"],
            ["Fresh whole spinach", "70 to 85 percent", "Stems, tough leaves"],
            ["Baby carrots (peeled)", "82 to 90 percent", "Skin, tops"],
            ["Whole pineapple", "45 to 55 percent", "Skin, core, crown"],
            ["Iceberg lettuce", "72 to 82 percent", "Core, outer leaves"],
          ]}
          testId="food-cost-table-yield"
        />
      </AnswerBlock>

      <AnswerBlock
        id="the-formulas"
        heading="The four formulas every chef should have on the pass"
        answer="Food cost percent equals plate cost divided by sell price, times one hundred. Ideal sell price equals plate cost divided by target food cost percent. Margin equals sell price minus plate cost. Contribution equals margin multiplied by units sold in the period. Memorise these before any software."
      >
        <DataTable
          caption="Core food cost formulas"
          columns={["Metric", "Formula", "Worked example"]}
          rows={[
            ["Plate cost", "Σ (portion × usable cost per unit)", "0.18 kg × 42.00 per kg = 7.56"],
            ["Food cost %", "(Plate cost ÷ Sell price) × 100", "(7.56 ÷ 24.00) × 100 = 31.5 %"],
            ["Ideal sell price", "Plate cost ÷ Target food cost %", "7.56 ÷ 0.30 = 25.20"],
            ["Plate margin", "Sell price minus plate cost", "24.00 minus 7.56 equals 16.44"],
            ["Period contribution", "Margin × Units sold", "16.44 × 210 = 3452.40"],
            ["Usable cost per unit", "Pack price ÷ (pack size × yield %)", "38.00 ÷ (2 kg × 0.85) = 22.35"],
          ]}
          testId="food-cost-table-formulas"
        />
      </AnswerBlock>

      <AnswerBlock
        id="lowering-food-cost"
        heading="How to lower food cost without cheapening the plate"
        answer="Do the boring work before you touch a menu price. Reweigh three high-volume portions, cross-utilise the trim from your two most expensive proteins, and renegotiate the top five SKUs. Only after those levers are pulled should you raise the sell price by one or two dollars."
        softwareNote={<>Culinaire Specialist automatically flags every plate whose food cost drifts above 35 percent and shows you the exact ingredient that moved. Pair it with the Batch Yield Precision Tool to log a real prep yield in one tap.</>}
      />
    </ResourceLayout>
  );
}
